package kz.dentalcrm.app.screens.employees.edit

import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_employee_deleted
import dentalcrm.composeapp.generated.resources.snackbar_employee_updated
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.ui.ScreenState
import kz.dentalcrm.app.core.utils.stateHandle
import kz.dentalcrm.app.data.network.request_model.AddEmployeeBody
import kz.dentalcrm.app.domain.AppointmentRepository
import kz.dentalcrm.app.screens.employees.edit.mvi.EditEmployeeSideEffect
import kz.dentalcrm.app.screens.employees.edit.mvi.EditEmployeeState
import kz.dentalcrm.app.screens.employees.edit.mvi.EditEmployeeViewEvent
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject

class EditEmployeeViewModel : BaseStateViewModel<
        EditEmployeeViewEvent,
        EditEmployeeState,
        EditEmployeeSideEffect>() {

    private val appointmentRepository: AppointmentRepository by inject()

    override fun setInitialState(): EditEmployeeState = EditEmployeeState()

    override fun handleEvents(event: EditEmployeeViewEvent) {
        when (event) {
            is EditEmployeeViewEvent.LoadInitialData -> {
                loadInitialData(event.employeeId)
            }

            is EditEmployeeViewEvent.UpdateName -> {
                setState {
                    copy(
                        name = event.value,
                        nameError = if (event.value.isEmpty()) "Имя обязательно" else ""
                    )
                }
            }

            is EditEmployeeViewEvent.UpdateSurname -> {
                setState {
                    copy(
                        surname = event.value,
                        surnameError = if (event.value.isEmpty()) "Фамилия обязательна" else ""
                    )
                }
            }

            is EditEmployeeViewEvent.UpdatePatronymic -> {
                setState {
                    copy(patronymic = event.value)
                }
            }

            is EditEmployeeViewEvent.UpdatePhone -> {
                setState {
                    copy(
                        phone = event.value,
                        phoneError = if (event.value.isEmpty()) "Телефон обязателен" else ""
                    )
                }
            }

            is EditEmployeeViewEvent.UpdateGender -> {
                setState {
                    copy(gender = event.value)
                }
            }

            is EditEmployeeViewEvent.UpdateBirthDate -> {
                setState {
                    copy(birthDate = event.value)
                }
            }

            is EditEmployeeViewEvent.UpdateColor -> {
                setState {
                    copy(color = event.value)
                }
            }

            is EditEmployeeViewEvent.UpdatePositions -> {
                setState {
                    copy(selectedPositions = event.positions)
                }
            }

            is EditEmployeeViewEvent.UpdateServices -> {
                setState {
                    copy(selectedServices = event.services)
                }
            }

            is EditEmployeeViewEvent.UpdateCreateAccount -> {
                setState {
                    copy(createAccount = event.value)
                }
            }

            is EditEmployeeViewEvent.UpdateEmail -> {
                setState {
                    copy(
                        email = event.value,
                        emailError = if (event.value.isEmpty()) "Email обязателен" else ""
                    )
                }
            }

            is EditEmployeeViewEvent.UpdatePassword -> {
                setState {
                    copy(
                        password = event.value,
                        passwordError = if (event.value.isEmpty()) "Пароль обязателен" else ""
                    )
                }
            }

            is EditEmployeeViewEvent.TogglePermission -> {
                setState {
                    val newPermissions = if (selectedPermissions.contains(event.permission)) {
                        selectedPermissions - event.permission
                    } else {
                        selectedPermissions + event.permission
                    }
                    copy(selectedPermissions = newPermissions)
                }
            }

            is EditEmployeeViewEvent.UpdateEmployee -> {
                updateEmployee()
            }

            is EditEmployeeViewEvent.DeleteEmployee -> {
                deleteEmployee()
            }

            is EditEmployeeViewEvent.ResetState -> {
                setState { EditEmployeeState() }
            }
        }
    }

    private fun loadInitialData(employeeId: Int) {
        setState { copy(screenState = ScreenState.Loading, employeeId = employeeId) }

        launch {
            var positionsLoaded = false
            var servicesLoaded = false
            var permissionsLoaded = false
            var employeeLoaded = false
            var hasError = false

            // Load positions
            appointmentRepository.getPositions().stateHandle(
                onError = { _, msg ->
                    hasError = true
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.ShowError(msg ?: "Failed to load positions") }
                },
                onSuccess = { positions ->
                    positionsLoaded = true
                    setState {
                        copy(
                            availablePositions = positions,
                            screenState = if ((servicesLoaded && permissionsLoaded && employeeLoaded) || hasError) ScreenState.Idle else ScreenState.Loading
                        )
                    }
                }
            )

            // Load services
            appointmentRepository.getServicesArray().stateHandle(
                onError = { _, msg ->
                    hasError = true
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.ShowError(msg ?: "Failed to load services") }
                },
                onSuccess = { services ->
                    servicesLoaded = true
                    setState {
                        copy(
                            availableServices = services,
                            screenState = if ((positionsLoaded && permissionsLoaded && employeeLoaded) || hasError) ScreenState.Idle else ScreenState.Loading
                        )
                    }
                }
            )

            // Load permissions
            appointmentRepository.getPermissions().stateHandle(
                onError = { _, msg ->
                    hasError = true
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect {
                        EditEmployeeSideEffect.ShowError(
                            msg ?: "Failed to load permissions"
                        )
                    }
                },
                onSuccess = { permissions ->
                    permissionsLoaded = true
                    setState {
                        copy(
                            availablePermissions = permissions,
                            screenState = if ((positionsLoaded && servicesLoaded && employeeLoaded) || hasError) ScreenState.Idle else ScreenState.Loading
                        )
                    }
                }
            )

            // Load employee
            appointmentRepository.getEmployee(employeeId).stateHandle(
                onError = { _, msg ->
                    hasError = true
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.ShowError(msg ?: "Failed to load employee") }
                },
                onSuccess = { employee ->
                    employeeLoaded = true
                    if (employee != null) {
                        setState {
                            copy(
                                name = employee.name ?: "",
                                surname = employee.surname ?: "",
                                patronymic = employee.patronymic,
                                phone = employee.phone ?: "",
                                gender = employee.gender ?: "",
                                birthDate = employee.birth_date ?: "",
                                color = employee.color ?: "#0EA5E9",
                                selectedPositions = employee.positions ?: emptyList(),
                                selectedServices = employee.services ?: emptyList(),
                                createAccount = employee.account != null,
                                hasAccount = employee.account != null,
                                email = employee.account?.email ?: "",
                                selectedPermissions = employee.account?.permissions ?: emptyList(),
                                screenState = if ((positionsLoaded && servicesLoaded && permissionsLoaded) || hasError) ScreenState.Idle else ScreenState.Loading
                            )
                        }
                    } else {
                        setState { copy(screenState = ScreenState.Idle) }
                        setEffect { EditEmployeeSideEffect.ShowError("Employee not found") }
                    }
                }
            )
        }
    }

    private fun updateEmployee() {
        val state = viewState.value

        // Validation
        if (state.name.isEmpty()) {
            setState { copy(nameError = "Имя обязательно") }
            return
        }
        if (state.surname.isEmpty()) {
            setState { copy(surnameError = "Фамилия обязательна") }
            return
        }
        if (state.phone.isEmpty()) {
            setState { copy(phoneError = "Телефон обязателен") }
            return
        }
        if (state.gender.isEmpty()) {
            setEffect { EditEmployeeSideEffect.ShowError("Пол обязателен") }
            return
        }
        if (state.birthDate.isEmpty()) {
            setEffect { EditEmployeeSideEffect.ShowError("Дата рождения обязательна") }
            return
        }

        // Validate account creation fields if enabled
        if (state.createAccount) {
            if (state.email.isEmpty()) {
                setState { copy(emailError = "Email обязателен") }
                return
            }
            if (!state.hasAccount && state.password.isEmpty()) {
                setState { copy(passwordError = "Пароль обязателен") }
                return
            }
        }

        setState { copy(screenState = ScreenState.Loading) }

        val employeeBody = AddEmployeeBody(
            name = state.name,
            surname = state.surname,
            patronymic = state.patronymic,
            phone = state.phone,
            gender = state.gender,
            birth_date = state.birthDate,
            color = state.color,
            positions = state.selectedPositions.ifEmpty { null },
            services = state.selectedServices.ifEmpty { null },
            email = if (state.createAccount) state.email else null,
            password = if (state.createAccount && state.password.isNotEmpty()) state.password else null,
            permissions = if (state.createAccount && state.selectedPermissions.isNotEmpty()) state.selectedPermissions else null,
            create_account = state.createAccount,
            role_id = state.roleId
        )

        launch {
            appointmentRepository.updateEmployee(state.employeeId, employeeBody).stateHandle(
                onError = { _, msg ->
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.ShowError(msg ?: "Failed to update employee") }
                },
                onFailure = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.ShowError("Failed to update employee") }
                },
                onSuccess = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_employee_updated), SnackbarEvent.SnackbarType.Green))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.UpdateSuccess }
                }
            )
        }
    }

    private fun deleteEmployee() {
        val state = viewState.value
        setState { copy(screenState = ScreenState.Loading) }

        launch {
            appointmentRepository.deleteEmployee(state.employeeId).stateHandle(
                onError = { _, msg ->
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.ShowError(msg ?: "Failed to delete employee") }
                },
                onFailure = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.error_message), SnackbarEvent.SnackbarType.Red))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.ShowError("Failed to delete employee") }
                },
                onSuccess = {
                    SnackbarController.sendEvent(SnackbarEvent(getString(Res.string.snackbar_employee_deleted), SnackbarEvent.SnackbarType.Green))
                    setState { copy(screenState = ScreenState.Idle) }
                    setEffect { EditEmployeeSideEffect.DeleteSuccess }
                }
            )
        }
    }
}
